import * as React from "react";
import { cn } from "../utils";
import { MaybeObjectLink } from "./object-link";

export interface FeedListProps<R> {
  rows: R[];
  rowKey: (row: R) => string | number;
  /**
   * Optional leading cell before the text column — e.g. a severity dot or an
   * icon. Kept shrink-0 so the text column still truncates.
   */
  leading?: (row: R) => React.ReactNode;
  /**
   * Primary (top) line for each row. Rendered as `text-foreground`. Required.
   */
  primary: (row: R) => React.ReactNode;
  /** Optional secondary (bottom) line. Rendered as `text-muted-foreground`. */
  secondary?: (row: R) => React.ReactNode;
  /**
   * Optional trailing right-aligned cell (e.g. relative time, count badge).
   */
  trailing?: (row: R) => React.ReactNode;
  /** When set, each row becomes a Next.js link. */
  rowHref?: (row: R) => string | undefined;
  /** Class on each <li>. */
  rowClassName?: (row: R, index: number) => string | undefined;
  emptyMessage?: React.ReactNode;
  className?: string;
}

// Row metrics come from the RowShell primitive (docs/design/VERTICAL-RHYTHM-PRD.md
// §T3): the crowned px-4 gutter + the shared `--row-py` rhythm, no first-row
// top-trim. FeedList stays a `<ul>/<li>` (see the design rule below) so it can't
// mount <RowShell> directly — it adopts its class metrics here. The alternating
// row fill + hover affordance are FeedList's own, kept as-is.
const ROW_BASE =
  "flex items-center gap-3 px-[var(--content-gutter)] py-[var(--row-py)] type-body border-b border-border last:border-b-0 even:bg-foreground/[0.025] transition-colors";

/**
 * Accessible one-column list (`<ul>`) styled to match the visual conventions
 * of `<DataTable>` — alternating row backgrounds, hairline dividers, hover
 * affordance. Used for review queues, attention rails, overdue lists — any
 * row-of-records UI that has at most two text lines and doesn't justify the
 * full table primitive.
 *
 * Design rule: this stays a `<ul>/<li>` rather than a one-column `<table>`
 * because screen readers should announce "list, N items" rather than
 * "table, 1 column, N rows" for this pattern.
 */
export function FeedList<R>({
  rows,
  rowKey,
  leading,
  primary,
  secondary,
  trailing,
  rowHref,
  rowClassName,
  emptyMessage = "Nothing here yet.",
  className,
}: FeedListProps<R>) {
  if (rows.length === 0) {
    return (
      <div
        className={cn(
          "px-[var(--prose-gutter)] py-[var(--section-gap)] type-body text-muted-foreground",
          className,
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className={cn("flex flex-col", className)}>
      {rows.map((row, i) => {
        const href = rowHref?.(row);
        const sec = secondary?.(row);
        const trail = trailing?.(row);
        const rowClass = cn(
          ROW_BASE,
          href && "hover:bg-accent/40",
          rowClassName?.(row, i),
        );
        const lead = leading?.(row);
        const body = (
          <>
            {lead ? <span className="shrink-0">{lead}</span> : null}
            <span className="flex flex-col gap-0.5 min-w-0 flex-1">
              <span className="text-foreground truncate">{primary(row)}</span>
              {sec ? (
                <span className="type-subtext text-muted-foreground truncate">
                  {sec}
                </span>
              ) : null}
            </span>
            {trail ? (
              <span className="text-muted-foreground tabular-nums whitespace-nowrap shrink-0">
                {trail}
              </span>
            ) : null}
          </>
        );
        return href ? (
          <li key={rowKey(row)}>
            {/* Peeks when the row points at an object, navigates otherwise. */}
            <MaybeObjectLink href={href} className={rowClass}>
              {body}
            </MaybeObjectLink>
          </li>
        ) : (
          <li key={rowKey(row)} className={rowClass}>
            {body}
          </li>
        );
      })}
    </ul>
  );
}
